import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/auth';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const login = useAuthStore((s) => s.login);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const clearError = useAuthStore((s) => s.clearError);
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
  const verifiedFlag = searchParams.get('verified') === '1';

  // Clear any error if the user starts typing again
  useEffect(() => {
    if (unverifiedEmail) setUnverifiedEmail(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setUnverifiedEmail(null);
    try {
      await login({ email, password });
      const user = useAuthStore.getState().user;
      if (!user) return;
      const redirectTo = from || (user.role === 'admin' ? '/admin' : `/${user.role}`);
      navigate(redirectTo, { replace: true });
    } catch (err: unknown) {
      // If backend signaled the user is unverified, surface a resend action.
      const response = (err as { response?: { data?: Record<string, unknown> } })?.response;
      const data = response?.data;
      if (data && data.code === 'email_not_verified') {
        const blockedEmail = typeof data.email === 'string' ? data.email : email;
        setUnverifiedEmail(blockedEmail);
      }
      // Other errors are surfaced by the store via `error`
    }
  };

  const handleResend = async () => {
    if (!unverifiedEmail) return;
    setResendStatus('sending');
    try {
      await authService.resendVerificationByEmail(unverifiedEmail);
      setResendStatus('sent');
    } catch {
      setResendStatus('idle');
    }
  };

  return (
    <>
      <div className="mt-2 flex justify-center">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-blue-600 transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back to home
        </Link>
      </div>
      <p className="mt-2 text-center text-sm text-slate-500">Sign in to your account</p>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-sm border border-slate-100 rounded-2xl sm:px-10">
          {verifiedFlag && (
            <div role="status" className="mb-4 rounded-xl bg-emerald-50 p-4">
              <p className="text-sm text-emerald-700 font-medium">
                Email verified. You can sign in now.
              </p>
            </div>
          )}

          {unverifiedEmail ? (
            <div role="alert" className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-4">
              <p className="text-sm text-amber-800 font-medium">
                Your email isn't verified yet.
              </p>
              <p className="mt-1 text-xs text-amber-700">
                Click the link we sent to <span className="font-semibold">{unverifiedEmail}</span> to activate your account. Check spam if you don't see it.
              </p>
              {resendStatus === 'sent' ? (
                <p className="mt-2 text-xs text-emerald-700 font-semibold">
                  Verification email sent.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendStatus === 'sending'}
                  className="mt-2 text-xs font-semibold text-amber-800 underline hover:text-amber-900 disabled:opacity-50"
                >
                  {resendStatus === 'sending' ? 'Sending...' : 'Resend verification email'}
                </button>
              )}
            </div>
          ) : (
            error && (
              <div role="alert" className="mb-4 rounded-xl bg-red-50 p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-500 font-medium">
                Forgot your password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-sm shadow-blue-500/25 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Don't have an account?{' '}
            <Link to="/signup" className="text-blue-600 hover:text-blue-500 font-semibold">
              Sign up
            </Link>
          </p>
        </div>
        {/* Demo-credential quick-fill block intentionally removed — the
            seeded accounts (Sentinel Shield / Apex Property / admin)
            still exist in the database for internal demos but we don't
            advertise them on the public login page. */}
      </div>
    </>
  );
}
